import { useCallback, useEffect, useState, useRef, ReactNode } from 'react';
import { User } from 'oidc-client-ts';
import { useQueryClient } from '@tanstack/react-query';
import { authConfig } from '@/config/auth.config';
import { config } from '@/config/environment';
import { isAllowedOrigin } from '@/utils/ndEnvironment';
import {
  isTeamsAuthPopup,
  isTeamsAuthPopupStart,
  completeTeamsAuthPopup,
  isTeamsTab,
  markTeamsSignedOut,
  teamsSignInPrompt,
} from '@/utils/teamsHost';
import { isIframeEnvironment } from '@/utils/ndEnvironment';
import {
  isExtensionHost,
  markExtensionSignedOut,
  isOutlookAuthPopup,
  isOutlookAuthPopupStart,
  completeOutlookAuthPopup,
} from '@/utils/extensionHost';
import { setTokenGetter } from '@/api';
import { userManager } from './userManager';
import { AuthContext, type AuthContextType } from './AuthContext';
import {
  getValidAccessToken,
  revalidateSession,
  triggerSessionExpired,
  resetSessionExpiry,
  suppressSessionExpiry,
  unsuppressSessionExpiry,
} from './sessionExpiry';

// Login popup: the firm the opener asked to sign in to (see loadUser and login).
const LOGIN_TENANT_KEY = 'motr.login.tenant';
const LOGIN_PROMPT_KEY = 'motr.login.prompt';

// Track if callback is being processed to prevent double redemption (React Strict Mode)
let isProcessingCallback = false;

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [permissions, setPermissions] = useState<string[]>([]);
  const [roles, setRoles] = useState<string[]>([]);
  const [features, setFeatures] = useState<Record<string, string>>({});
  const [featuresLoaded, setFeaturesLoaded] = useState(false);
  // Distinct from featuresLoaded, which flips true even when the fetch failed (to unblock
  // the UI). Only this flag means "the server actually told us this user's permissions",
  // so an empty permission set can be trusted as real rather than as a network blip.
  const [permissionsResolved, setPermissionsResolved] = useState(false);
  const [currentTenantName, setCurrentTenantName] = useState<string | null>(null);
  const initRef = useRef(false);
  const qc = useQueryClient();

  useEffect(() => {
    // Prevent double initialization in React Strict Mode
    if (initRef.current) return;
    initRef.current = true;

    const loadUser = async () => {
      try {
        // If this window was opened as a login popup (by the iframe),
        // clear any stale OIDC user data from the popup's first-party
        // localStorage. Without this, the popup may find a valid token
        // from a previous session and skip the OIDC flow entirely —
        // never sending auth data back to the iframe.
        const isLoginPopup = window.location.search.includes('popup=login');
        // A Teams sign-in popup may have no opener (Teams desktop), so it is recognised by its URL.
        // An Outlook sign-in dialog may have no usable opener either (Office opens it).
        if (isLoginPopup && (window.opener || isTeamsAuthPopupStart() || isOutlookAuthPopupStart())) {
          // The firm to sign in to, when the opener knows it (Chrome extension: from the Gmail
          // address). Kept for login() below, which runs after this URL is stripped.
          const requestedTenant = new URLSearchParams(window.location.search).get('__tenant');
          try {
            if (requestedTenant !== null) sessionStorage.setItem(LOGIN_TENANT_KEY, requestedTenant);
            else sessionStorage.removeItem(LOGIN_TENANT_KEY);
            // prompt=login after a sign-out in the opener: ask for the password again.
            const requestedPrompt = new URLSearchParams(window.location.search).get('prompt');
            if (requestedPrompt === 'login') sessionStorage.setItem(LOGIN_PROMPT_KEY, requestedPrompt);
            else sessionStorage.removeItem(LOGIN_PROMPT_KEY);
          } catch { /* storage blocked: sign in without a firm */ }
          await userManager.removeUser();
          // Strip the popup param so it doesn't interfere with later checks
          window.history.replaceState({}, document.title, window.location.pathname);
        }

        // Handle redirect callback
        if (window.location.search.includes('code=') && !isProcessingCallback) {
          isProcessingCallback = true;

          // Store the current URL before clearing (needed for callback)
          const callbackUrl = window.location.href;

          // Clear URL immediately to prevent double processing on refresh
          window.history.replaceState({}, document.title, window.location.pathname);

          try {
            const user = await userManager.signinRedirectCallback(callbackUrl);
            setUser(user);
            // A fresh token means any prior session-expired modal is now stale.
            resetSessionExpiry();

            // If we came here from the session-expired modal (or any deep link),
            // restore the saved location so the user lands back where they were.
            // This runs before AppRouter mounts, so BrowserRouter reads the
            // restored URL on first render.
            const returnUrl = (user.state as { returnUrl?: string } | undefined)?.returnUrl;
            if (returnUrl) {
              try {
                const target = new URL(returnUrl);
                if (target.origin === window.location.origin) {
                  window.history.replaceState(
                    {},
                    document.title,
                    target.pathname + target.search + target.hash
                  );
                }
              } catch { /* ignore malformed return URL */ }
            }

            // Ensure apiClient has the token getter BEFORE re-fetching tenant data
            // (normally set later in AppShell, but needed here for the refetch).
            // Uses the renew-or-fail getter so a near-expiry token is refreshed
            // rather than silently sent stale.
            setTokenGetter(getValidAccessToken);

            // Clear cached tenant settings, theme override, and theme query
            // so they re-fetch with the authenticated user's token
            qc.removeQueries({ queryKey: ['tenant-settings'] });
            qc.removeQueries({ queryKey: ['settings', 'theme'] });
            // Clear cached tenant theme from localStorage (contains logo URLs from previous session)
            try { localStorage.removeItem('motr_tenant_theme_cache'); } catch { /* ignore */ }

            // If this window was opened as a popup (e.g., iframe login flow),
            // send the actual OIDC tokens to the parent via postMessage.
            // This mirrors Angular's home.component.ts approach: the parent
            // iframe has PARTITIONED localStorage (third-party context), so
            // we must explicitly pass the token data — NOT just a signal.
            const storageKey = `oidc.user:${authConfig.authority}:${authConfig.client_id}`;
            const userJson = localStorage.getItem(storageKey);

            // Outlook sign-in dialog: the add-in's relay page hands the user to the task pane.
            // Before the opener branch, which would post to Office's window and close the dialog.
            if (isOutlookAuthPopup()) {
              completeOutlookAuthPopup(storageKey);
              return;
            }

            // Teams sign-in popup: hand the user back through Teams, which closes the popup.
            // Must run before the opener branch — in Teams on the web the opener is the Teams
            // page itself, and that branch would close the popup first.
            if (isTeamsAuthPopup()) {
              await completeTeamsAuthPopup({ oidcStorageKey: storageKey, oidcUserJson: userJson });
              return;
            }

            if (window.opener) {
              try {
                // Build the oidc-client-ts storage key so the parent can
                // store the user object under the correct key.
                const openerOrigin = (() => {
                  try { return window.opener?.location?.origin; }
                  catch { return window.location.origin; }
                })();

                if (openerOrigin && isAllowedOrigin(openerOrigin)) {
                  window.opener.postMessage({
                    type: 'motr:auth-complete',
                    // Send the serialized user object so the parent can
                    // store it in its own (partitioned) localStorage.
                    oidcStorageKey: storageKey,
                    oidcUserJson: userJson,
                  }, openerOrigin);
                }
              } catch (err) {
                console.error('[Auth:Popup] Failed to relay auth data to opener:', err);
              }
            }

            // Also broadcast via BroadcastChannel as a fallback.
            // window.opener can become null after cross-origin OIDC
            // redirects (e.g., COOP headers on the auth server).
            try {
              const bc = new BroadcastChannel('motr:auth');
              bc.postMessage({
                type: 'motr:auth-complete',
                oidcStorageKey: storageKey,
                oidcUserJson: userJson,
              });
              bc.close();
            } catch {
              // BroadcastChannel not supported or partitioned — ignore
            }

            if (window.opener) {
              // Close the popup after a brief delay
              setTimeout(() => {
                try { window.close(); } catch { /* ignore */ }
              }, 500);
              return;
            }
          } catch (callbackErr) {
            // If code was already redeemed, try to get existing user
            if (callbackErr instanceof Error && callbackErr.message.includes('already been redeemed')) {
              console.warn('Auth code already redeemed, checking for existing session');
              const existingUser = await userManager.getUser();
              if (existingUser) {
                setUser(existingUser);
              } else {
                throw callbackErr;
              }
            } else {
              throw callbackErr;
            }
          } finally {
            isProcessingCallback = false;
          }
        } else if (!window.location.search.includes('code=')) {
          // Check for existing session
          const user = await userManager.getUser();
          setUser(user);
        }
      } catch (err) {
        console.error('Auth initialization error:', err);
        setError(err instanceof Error ? err : new Error('Authentication failed'));
        isProcessingCallback = false;
      } finally {
        setIsLoading(false);
      }
    };

    loadUser();

    // Disable silent renewal during impersonation to prevent the admin's
    // refresh token from overwriting the impersonated user's access token
    const isImpersonating = sessionStorage.getItem('motr_impersonating') === 'true';
    if (isImpersonating) {
      userManager.stopSilentRenew();
      //console.log('[Impersonation:Auth] Silent renewal disabled during impersonation');

      // Set up a timer to end impersonation before token expires
      const checkExpiry = async () => {
        const currentUser = await userManager.getUser();
        if (currentUser?.expires_at) {
          const now = Math.floor(Date.now() / 1000);
          const secondsRemaining = currentUser.expires_at - now;
          // End impersonation 60 seconds before expiry (or immediately if already past)
          const bufferSeconds = 60;
          const timeoutMs = Math.max(0, (secondsRemaining - bufferSeconds) * 1000);

          //console.log('[Impersonation:Auth] Token expires in', secondsRemaining, 'seconds. Will end impersonation in', Math.round(timeoutMs / 1000), 'seconds.');

          return window.setTimeout(() => {
            //console.warn('[Impersonation:Auth] Impersonation token expiring — ending impersonation session');
            alert('Impersonation session has expired. You will be returned to your original account.');
            // Import dynamically to avoid circular dependency
            import('@/auth/impersonation').then(({ endImpersonation }) => {
              endImpersonation();
            });
          }, timeoutMs);
        }
        return undefined;
      };

      let expiryTimerId: number | undefined;
      checkExpiry().then(id => { expiryTimerId = id; });

      // Store cleanup ref for the timer
      const originalCleanup = () => {
        if (expiryTimerId !== undefined) {
          clearTimeout(expiryTimerId);
        }
      };
      // We'll add this to the effect cleanup below via a ref
      (window as unknown as Record<string, unknown>).__motr_impersonation_cleanup = originalCleanup;
    }

    // Set up event handlers
    const handleUserLoaded = (loadedUser: User) => {
      const isImp = sessionStorage.getItem('motr_impersonating') === 'true';
      if (isImp) {
        // Decode token to check if silent renewal replaced the impersonated token
        const decodeClaims = (token: string): { sub: string | null; unique_name: string | null } => {
          try {
            const payload = token.split('.')[1];
            const decoded = JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')));
            return { sub: decoded.sub || null, unique_name: decoded.unique_name || null };
          } catch { return { sub: null, unique_name: null }; }
        };
        const claims = decodeClaims(loadedUser.access_token);
        const expectedUserId = sessionStorage.getItem('motr_impersonated_user_id');

        // If sub changed (renewal replaced impersonated token with admin token), end impersonation
        if (expectedUserId && claims.sub && claims.sub !== expectedUserId) {
          // console.error('[Impersonation:Auth] Token was replaced by silent renewal! Ending impersonation.', {
          //   tokenSub: claims.sub,
          //   expectedUserId,
          // });
          alert('Impersonation session was interrupted. Returning to your original account.');
          import('@/auth/impersonation').then(({ endImpersonation }) => {
            endImpersonation();
          });
          return; // Don't update state with wrong user
        }
      }
      setUser(loadedUser);
      setError(null);
      // Token (re)loaded successfully — the session is healthy again.
      resetSessionExpiry();
    };

    const handleUserUnloaded = () => {
      setUser(null);
      setPermissions([]);
      setRoles([]);
      setFeatures({});
      setFeaturesLoaded(false);
      setPermissionsResolved(false);
    };

    const handleSilentRenewError = (error: Error) => {
      const isImp = sessionStorage.getItem('motr_impersonating') === 'true';
      console.error('[Auth] Silent renew error:', {
        message: error.message,
        isImpersonating: isImp,
        impersonatedUser: isImp ? sessionStorage.getItem('motr_impersonated_user_name') : null,
      });
      setError(error);
      // Automatic renewal failed — the session can no longer be kept alive
      // silently. Drive the session-expired flow. Impersonation has its own
      // expiry handling, so don't show the generic modal there.
      if (!isImp) {
        triggerSessionExpired();
      }
    };

    // Proactive idle detection: oidc-client-ts fires this when the access token
    // hard-expires and automatic silent renew could not recover it — even with
    // no user interaction. Surface the session-expired modal.
    const handleAccessTokenExpired = () => {
      if (sessionStorage.getItem('motr_impersonating') === 'true') return;
      triggerSessionExpired();
    };

    const handleUserSignedOut = () => {
      setUser(null);
      setPermissions([]);
      setRoles([]);
      setFeatures({});
      setFeaturesLoaded(false);
      setPermissionsResolved(false);
    };

    userManager.events.addUserLoaded(handleUserLoaded);
    userManager.events.addUserUnloaded(handleUserUnloaded);
    userManager.events.addSilentRenewError(handleSilentRenewError);
    userManager.events.addUserSignedOut(handleUserSignedOut);
    userManager.events.addAccessTokenExpired(handleAccessTokenExpired);

    return () => {
      userManager.events.removeUserLoaded(handleUserLoaded);
      userManager.events.removeUserUnloaded(handleUserUnloaded);
      userManager.events.removeSilentRenewError(handleSilentRenewError);
      userManager.events.removeUserSignedOut(handleUserSignedOut);
      userManager.events.removeAccessTokenExpired(handleAccessTokenExpired);
      // Clean up impersonation expiry timer
      const cleanup = (window as unknown as Record<string, unknown>).__motr_impersonation_cleanup as (() => void) | undefined;
      if (cleanup) {
        cleanup();
        delete (window as unknown as Record<string, unknown>).__motr_impersonation_cleanup;
      }
    };
  }, []);

  // Proactive idle detection (Tier B): when the tab regains focus after the
  // machine was asleep / the tab was backgrounded, the access token may have
  // hard-expired while no timers were running. Re-validate the session and
  // surface the modal if it can't be silently renewed.
  useEffect(() => {
    const recheck = () => {
      if (document.visibilityState !== 'visible') return;
      void revalidateSession();
    };
    document.addEventListener('visibilitychange', recheck);
    window.addEventListener('focus', recheck);
    return () => {
      document.removeEventListener('visibilitychange', recheck);
      window.removeEventListener('focus', recheck);
    };
  }, []);

  // Load permissions when user is authenticated
  useEffect(() => {
    const loadPermissions = async () => {
      if (!user) {
        setPermissions([]);
        setFeaturesLoaded(false);
      setPermissionsResolved(false);
        setCurrentTenantName(null);
        return;
      }

      try {
        const token = user.access_token;
        // Use dynamic API URL from config (supports tenant subdomains)
        const response = await fetch(`${config.apiUrl}/api/abp/application-configuration`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (response.ok) {
          const appConfig = await response.json();
          const grantedPermissions = Object.entries(appConfig.auth?.grantedPolicies || {})
            .filter(([, granted]) => granted)
            .map(([permission]) => permission);
          setPermissions(grantedPermissions);

          // Extract user roles from ABP currentUser
          const userRoles: string[] = appConfig.currentUser?.roles || [];
          setCurrentTenantName(appConfig.currentTenant?.isAvailable ? appConfig.currentTenant.name || null : null);
          setRoles(userRoles);

          // Extract ABP features (e.g., motr.PremiumFeatures.TimeTracking.Enabled)
          const featureValues: Record<string, string> = {};
          const featuresObj = appConfig.features?.values || {};
          Object.entries(featuresObj).forEach(([key, value]) => {
            featureValues[key] = String(value);
          });
          setFeatures(featureValues);
          setFeaturesLoaded(true);
          setPermissionsResolved(true);
        }
      } catch (err) {
        console.error('Failed to load permissions:', err);
        setFeaturesLoaded(true); // Mark loaded even on error to unblock UI
      }
    };

    loadPermissions();
  }, [user]);

  const login = useCallback(async () => {
    try {
      // A Teams sign-in popup after an explicit sign-out asks for the password again.
      let prompt = isTeamsAuthPopup() ? teamsSignInPrompt() : undefined;
      try { prompt = prompt || sessionStorage.getItem(LOGIN_PROMPT_KEY) || undefined; } catch { /* ignore */ }
      // __tenant ('' = host) makes the auth server sign in to that firm, not the one in a cookie.
      let tenant: string | null = null;
      try { tenant = sessionStorage.getItem(LOGIN_TENANT_KEY); } catch { /* ignore */ }
      await userManager.signinRedirect({
        ...(prompt ? { prompt } : {}),
        ...(tenant !== null ? { extraQueryParams: { __tenant: tenant } } : {}),
      });
    } catch (err) {
      console.error('Login error:', err);
      setError(err instanceof Error ? err : new Error('Login failed'));
    }
  }, []);

  const logout = useCallback(async () => {
    // Teams / Outlook tab: the sign-out page cannot run inside the Microsoft 365 frame. End the
    // session here instead — revoke the refresh token, drop the stored user — and the tab falls
    // back to its sign-in card. The next sign-in asks for the password again.
    // The Chrome extension side panel frames the app the same way and signs out the same way.
    const inExtensionPanel = isExtensionHost() && isIframeEnvironment();
    if ((isTeamsTab() || inExtensionPanel) && isIframeEnvironment()) {
      suppressSessionExpiry();
      userManager.stopSilentRenew();
      try { await userManager.revokeTokens(['refresh_token']); } catch { /* best effort */ }
      await userManager.removeUser();
      if (inExtensionPanel) markExtensionSignedOut();
      else markTeamsSignedOut();
      setUser(null);
      unsuppressSessionExpiry();
      return;
    }
    try {
      // Set flag BEFORE signoutRedirect so AuthGuard won't race with a login redirect.
      // signoutRedirect() calls removeUser() internally, which clears the user and
      // triggers isAuthenticated=false. Without this flag, AuthGuard would immediately
      // call login() → signinRedirect(), aborting the logout navigation.
      setIsLoggingOut(true);
      // Intentional logout — suppress the session-expired modal so a trailing
      // 401 from an in-flight request can't flash it during sign-out navigation.
      suppressSessionExpiry();
      userManager.stopSilentRenew();
      await userManager.signoutRedirect();
    } catch (err) {
      console.error('Logout error:', err);
      setIsLoggingOut(false);
      // Sign-out navigation failed — we're staying on the page, so undo the
      // suppression or a genuine later expiry would be silently swallowed.
      unsuppressSessionExpiry();
      setError(err instanceof Error ? err : new Error('Logout failed'));
    }
  }, []);

  // Renew-or-fail token getter (Tier A): before returning, the controller
  // checks expiry and attempts an on-demand silent renew. If renewal fails it
  // triggers the session-expired flow and returns null, so an expired token is
  // never sent — the call fails cleanly instead of silently.
  const getAccessToken = useCallback(() => getValidAccessToken(), []);

  const hasPermission = useCallback(
    (permission: string) => {
      return permissions.includes(permission);
    },
    [permissions]
  );

  const hasFeature = useCallback(
    (featureName: string) => {
      return features[featureName] === 'true';
    },
    [features]
  );

  const value: AuthContextType = {
    user,
    isAuthenticated: !!user && !user.expired,
    isLoading,
    isLoggingOut,
    error,
    login,
    logout,
    getAccessToken,
    permissions,
    hasPermission,
    roles,
    features,
    featuresLoaded,
    permissionsResolved,
    hasFeature,
    currentTenantName,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
